import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Grid, Card, CardMedia, useTheme, useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import config from './config';
import { isBranchOpen } from './branchUtils';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import { fetchProducts } from './api';

const getImageUrls = (imageData) => {
  const fallbackUrl = '/logo-storesye.png';
  
  if (!imageData) {
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }
  
  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (imageData.thumbnail && imageData.medium && imageData.large) {
      return {
        thumbnail: imageData.thumbnail.startsWith('http') 
          ? imageData.thumbnail 
          : `${config.SITE_BASE_URL}${imageData.thumbnail}`,
        medium: imageData.medium.startsWith('http') 
          ? imageData.medium 
          : `${config.SITE_BASE_URL}${imageData.medium}`,
        large: imageData.large.startsWith('http') 
          ? imageData.large 
          : `${config.SITE_BASE_URL}${imageData.large}`
      };
    }
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }
  
  if (typeof imageData === 'string') {
    let fullUrl;
    
    if (imageData.startsWith('http')) {
      fullUrl = imageData;
    } else if (imageData.startsWith('data:image')) {
      fullUrl = imageData;
    } else if (imageData.startsWith('/images/')) {
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    } else if (imageData.startsWith('/')) {
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    } else {
      fullUrl = imageData;
    }
    
    return { thumbnail: fullUrl, medium: fullUrl, large: fullUrl };
  }
  
  return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
};

export default function HomeCustomProductsPreview() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedBranch } = useCart();
  const [branchStatus, setBranchStatus] = useState({ isOpen: true, nextOpen: '' });
  const [customProducts, setCustomProducts] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedBranch) {
      setBranchStatus(isBranchOpen(selectedBranch));
    }
  }, [selectedBranch]);

  useEffect(() => {
    fetchProducts(selectedBranch?.id).then(data => {
      setCustomProducts(data.filter(p => Array.isArray(p.SubProducts) && p.SubProducts.length > 0).slice(0, 3));
    });
  }, [selectedBranch]);

  if (!branchStatus.isOpen) {
    return null;
  }
  
  if (customProducts.length === 0) return null;

  return (
    <Box sx={{
      my: { xs: 3, sm: 4 },
      mx: { xs: 1.5, sm: 2 },
      p: { xs: 2, sm: 3 },
      bgcolor: theme.palette.background.paper,
      borderRadius: { xs: 3, sm: 4 },
      boxShadow: 1,
      border: '1px solid',
      borderColor: theme.palette.divider
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: { xs: 2, sm: 3 },
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Typography 
          variant="h5" 
          fontWeight="bold" 
          color={theme.palette.primary.main}
          sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
        >
          {t('home.customProducts', 'Customizable Products')}
        </Typography>
        <Typography 
          variant="body2" 
          color={theme.palette.primary.main}
          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
        >
          {t('home.createYourOwn', 'Create your own!')}
        </Typography>
      </Box>
      
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        {customProducts.map((item, index) => {
          const imageUrls = getImageUrls(item.ImageUrl);
          
          return (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card sx={{ 
                position: 'relative', 
                borderRadius: { xs: 2, sm: 3 }, 
                overflow: 'hidden', 
                boxShadow: 2, 
                bgcolor: theme.palette.background.paper,
                transition: 'transform 0.3s',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-4px)'
                },
                '&:active': {
                  transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)'
                }
              }}>
                <CardMedia
                  component="img"
                  height={isMobile ? "140" : "180"}
                  image={imageUrls.medium}
                  srcSet={`
                    ${imageUrls.thumbnail} 150w,
                    ${imageUrls.medium} 400w,
                    ${imageUrls.large} 1200w
                  `}
                  sizes="(max-width: 600px) 100vw, (max-width: 960px) 50vw, 33vw"
                  alt={item.ProductName}
                  loading="lazy"
                  sx={{ objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.src = '/logo-storesye.png';
                  }}
                />
              </Card>
            </Grid>
          );
        })}
      </Grid>
      
      <Button
        fullWidth
        variant="contained"
        onClick={() => navigate('/custom-products')}
        sx={{
          mt: { xs: 2, sm: 3 },
          borderRadius: { xs: 1.5, sm: 2 },
          color: theme.palette.custom?.white || '#fff',
          bgcolor: theme.palette.primary.main,
          py: { xs: 1.2, sm: 1.5 },
          fontSize: { xs: '0.85rem', sm: '1rem' },
          fontWeight: 'bold',
          transition: 'all 0.3s',
          '&:hover': {
            bgcolor: theme.palette.primary.dark,
            transform: isMobile ? 'none' : 'translateY(-2px)',
            boxShadow: isMobile ? 2 : 4
          },
          '&:active': {
            transform: isMobile ? 'scale(0.98)' : 'translateY(-2px)'
          }
        }}
      >
        {t('home.viewAllCustomProducts', 'View All Custom Products')}
      </Button>
    </Box>
  );
}
